import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Hourglass, ShieldX, UserCheck, Check, X, GraduationCap, Users, LogOut } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import guestApi from "@/lib/guestApi";
import useLiveKitMeeting from "@/hooks/useLiveKitMeeting";
import { LiveKitVideoTile } from "@/components/meeting/livekit/LiveKitVideoTile";
import { LiveKitScreenShareTile } from "@/components/meeting/livekit/LiveKitScreenShareTile";
import { LiveKitMeetingControls } from "@/components/meeting/livekit/LiveKitMeetingControls";
import { BreakoutRoomsPanel } from "@/components/meeting/livekit/BreakoutRoomsPanel";
import { VirtualBackgroundPanel } from "@/components/meeting/livekit/VirtualBackgroundPanel";
import { MeetingChatPanel } from "@/components/meeting/MeetingChatPanel";
import { playNotificationBeep } from "@/lib/notificationSound";

/** Enterprise Cloudivo Meet room — media transport via LiveKit Cloud's SFU.
 * Rendered directly by `MeetingRoomPage` for every meeting (the legacy P2P
 * mesh room has been fully removed). When `guestToken` is set, this renders
 * for an unauthenticated (no platform account) attendee who registered and
 * was approved — `isHost` is always false in that case. */
export function LiveKitMeetingRoom({ meetingId, meeting, isHost, onLeft, guestToken }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [handRaised, setHandRaised] = useState(false);
  const [breakoutsOpen, setBreakoutsOpen] = useState(false);
  const [backgroundOpen, setBackgroundOpen] = useState(false);
  const [activeBreakoutId, setActiveBreakoutId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingBusy, setRecordingBusy] = useState(false);
  const httpClient = guestToken ? guestApi : api;

  const {
    phase, selfInfo, peers, micOn, camOn, screenSharing, chatMessages, raisedHands, waitingRequests,
    localVideoTrack, localAudioTrack, localScreenTrack, toggleMic, toggleCam, toggleScreenShare, sendChat, toggleRaiseHand,
    admitParticipant, denyParticipant, switchRoom, breakoutRooms, breakoutSignal,
  } = useLiveKitMeeting(meetingId, meeting.meeting_type, isHost, guestToken);

  React.useEffect(() => {
    if (phase === "ended") { toast.info("This meeting has ended."); onLeft(); }
    if (phase === "error") toast.error("Connection error — please rejoin.");
  }, [phase, onLeft]);

  // Server-pushed breakout events — the host closing all rooms, or this participant
  // being auto-assigned to one, must take effect immediately even if the Breakout
  // Rooms panel is closed (unlike the manual self-select flow triggered from the panel).
  React.useEffect(() => {
    if (!breakoutSignal) return;
    if (breakoutSignal.type === "closed") {
      if (activeBreakoutId) {
        setActiveBreakoutId(null);
        switchRoom(null);
        toast.info("The host closed the breakout rooms — you're back in the main room.");
      }
    } else if (breakoutSignal.type === "assigned") {
      if (breakoutSignal.breakoutId !== activeBreakoutId) {
        setBreakoutsOpen(false);
        setActiveBreakoutId(breakoutSignal.breakoutId);
        switchRoom(breakoutSignal.breakoutId);
        toast.info(`You've been assigned to ${breakoutSignal.title || "a breakout room"}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakoutSignal]);

  // Host-only audio chime whenever a NEW attendee lands in the waiting room, so the
  // host notices the request even if they aren't looking at this tab.
  const prevWaitingCountRef = React.useRef(0);
  React.useEffect(() => {
    if (isHost && waitingRequests.length > prevWaitingCountRef.current) playNotificationBeep();
    prevWaitingCountRef.current = waitingRequests.length;
  }, [isHost, waitingRequests.length]);

  const handleLeave = async () => {
    if (isHost) { await api.post(`/meetings/${meetingId}/end`).catch(() => {}); }
    onLeft();
  };

  const handleToggleRecording = async () => {
    setRecordingBusy(true);
    try {
      if (isRecording) {
        await api.post(`/meetings/${meetingId}/recording/stop`);
        setIsRecording(false);
        toast.success("Recording stopped — it'll appear on your Recordings page in a minute or two, with an AI transcript & summary.");
      } else {
        await api.post(`/meetings/${meetingId}/recording/start`);
        setIsRecording(true);
        toast.success("Cloud recording started");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Recording action failed");
    } finally {
      setRecordingBusy(false);
    }
  };

  const raiseHand = () => {
    const next = !handRaised;
    setHandRaised(next);
    toggleRaiseHand(next);
  };

  const handleSwitchRoom = async (breakoutId) => {
    setBreakoutsOpen(false);
    setActiveBreakoutId(breakoutId);
    await switchRoom(breakoutId);
    toast.info(breakoutId ? "Joined breakout room" : "Back in the main room");
  };

  if (phase === "waiting_approval") {
    return (
      <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center gap-4 text-center px-6 z-50" data-testid="meeting-waiting-approval-screen">
        <Hourglass className="w-10 h-10 text-amber-400 animate-pulse" />
        <p className="text-white font-semibold">Waiting for the host to let you in...</p>
        <p className="text-sm text-slate-400">This meeting has a Waiting Room enabled.</p>
        <button onClick={onLeft} className="text-xs text-slate-500 hover:text-white mt-2">Cancel</button>
      </div>
    );
  }
  if (phase === "denied") {
    return (
      <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center gap-4 text-center px-6 z-50" data-testid="meeting-waiting-denied-screen">
        <ShieldX className="w-10 h-10 text-red-400" />
        <p className="text-white font-semibold">The host didn't admit you to this meeting.</p>
        <button onClick={onLeft} className="text-sm text-cyan-400 hover:text-cyan-300">Back to Meetings</button>
      </div>
    );
  }
  if (phase === "error") {
    return (
      <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center gap-4 text-center px-6 z-50" data-testid="livekit-connection-error-screen">
        <ShieldX className="w-10 h-10 text-red-400" />
        <p className="text-white font-semibold">Couldn't connect to this meeting.</p>
        <p className="text-sm text-slate-400">Please check your connection and try rejoining.</p>
        <button onClick={onLeft} className="text-sm text-cyan-400 hover:text-cyan-300">Back to Meetings</button>
      </div>
    );
  }

  const canUseCamera = meeting.meeting_type === "call" || isHost;
  // Only render a tile once there's at least SOME real signal for that identity (a name from
  // LiveKit/WS presence, or an actual media track) — guards against ever rendering a fully
  // empty ghost tile from a transient/out-of-order event.
  const peerEntries = Object.entries(peers).filter(([, p]) => p.name || p.videoTrack || p.audioTrack || p.screenTrack);

  return (
    <div className="fixed inset-0 bg-slate-950 flex flex-col z-50" data-testid="livekit-meeting-room-active">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <button
            onClick={handleLeave} data-testid="livekit-room-leave-to-home-btn" title="Leave & go back"
            className="flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-2.5 py-1 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Leave
          </button>
          <div className="flex items-center gap-2">
            {meeting.meeting_type === "class" ? <GraduationCap className="w-4 h-4 text-fuchsia-400" /> : <Users className="w-4 h-4 text-cyan-400" />}
            <span className="text-sm font-semibold text-white">{meeting.title}</span>
            <span className="text-xs text-slate-500">· {peerEntries.length + 1} in room</span>
            {activeBreakoutId && <span className="text-xs text-fuchsia-300 bg-fuchsia-500/10 px-2 py-0.5 rounded-full">Breakout</span>}
          </div>
        </div>
        {phase === "connecting" && <span className="text-xs text-slate-400 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Connecting...</span>}
        {isRecording && <span className="text-xs text-red-400 font-semibold flex items-center gap-1.5" data-testid="livekit-recording-indicator"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> Recording</span>}
      </div>

      {isHost && waitingRequests.length > 0 && (
        <div className="mx-4 mt-3 bg-amber-400/10 border border-amber-400/25 rounded-xl p-3 space-y-2" data-testid="waiting-room-requests-panel">
          <p className="text-xs font-bold text-amber-300 flex items-center gap-1.5"><UserCheck className="w-3.5 h-3.5" /> Waiting Room ({waitingRequests.length})</p>
          {waitingRequests.map((r) => (
            <div key={r.user_id} className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-1.5" data-testid={`waiting-request-${r.user_id}`}>
              <span className="text-sm text-white">{r.name}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => admitParticipant(r.user_id)} data-testid={`admit-btn-${r.user_id}`} className="flex items-center gap-1 text-xs font-semibold bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 px-2.5 py-1 rounded-lg"><Check className="w-3 h-3" /> Admit</button>
                <button onClick={() => denyParticipant(r.user_id)} data-testid={`deny-btn-${r.user_id}`} className="flex items-center gap-1 text-xs font-semibold bg-red-500/20 text-red-300 hover:bg-red-500/30 px-2.5 py-1 rounded-lg"><X className="w-3 h-3" /> Deny</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 p-4 overflow-y-auto">
          {localScreenTrack && <LiveKitScreenShareTile track={localScreenTrack} isSelf />}
          {peerEntries.filter(([, p]) => p.screenTrack).map(([uid, p]) => (
            <LiveKitScreenShareTile key={`screen-${uid}`} track={p.screenTrack} name={p.name} />
          ))}
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }} data-testid="livekit-video-grid">
            <LiveKitVideoTile videoTrack={localVideoTrack} audioTrack={localAudioTrack} name={selfInfo?.name || "You"} isHost={selfInfo?.is_host} isSelf audioOn={micOn} videoOn={camOn} />
            {peerEntries.map(([uid, p]) => (
              <LiveKitVideoTile key={uid} videoTrack={p.videoTrack} audioTrack={p.audioTrack} name={p.name} isHost={p.is_host} audioOn={p.audio !== false} videoOn={p.video !== false} />
            ))}
          </div>
        </div>
        {chatOpen && (
          <MeetingChatPanel messages={chatMessages} raisedHands={raisedHands} isHost={isHost} selfUserId={selfInfo?.user_id} onSend={sendChat} />
        )}
      </div>

      <LiveKitMeetingControls
        micOn={micOn} camOn={camOn} screenSharing={screenSharing} canUseCamera={canUseCamera}
        handRaised={handRaised} chatOpen={chatOpen} isHost={isHost}
        isRecording={isRecording} onToggleRecording={recordingBusy ? () => {} : handleToggleRecording}
        onOpenBreakouts={() => setBreakoutsOpen(true)} onOpenBackground={() => setBackgroundOpen(true)}
        onToggleMic={toggleMic} onToggleCam={toggleCam} onToggleScreenShare={toggleScreenShare}
        onToggleHand={raiseHand} onToggleChat={() => setChatOpen((v) => !v)} onLeave={handleLeave}
      />

      {breakoutsOpen && (
        <BreakoutRoomsPanel meetingId={meetingId} isHost={isHost} activeBreakoutId={activeBreakoutId} onSwitchRoom={handleSwitchRoom} onClose={() => setBreakoutsOpen(false)} httpClient={httpClient} liveBreakoutRooms={breakoutRooms} />
      )}
      {backgroundOpen && (
        <VirtualBackgroundPanel localVideoTrack={localVideoTrack} onClose={() => setBackgroundOpen(false)} />
      )}
    </div>
  );
}
